import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import type { InvoiceLineCreateInput } from '@/lib/invoicing/types';

// =============================================
// Line calculation helpers
// =============================================

function calculateLineBase(line: InvoiceLineCreateInput): number {
  const descuento = line.descuento_porcentaje ?? 0;
  return +(line.cantidad * line.precio_unitario * (1 - descuento / 100)).toFixed(2);
}

function calculateLineTaxes(base: number, line: InvoiceLineCreateInput) {
  const porcentajeIva = line.porcentaje_iva ?? 21;
  const cuotaIva = +(base * porcentajeIva / 100).toFixed(2);
  const recargoCuota = line.recargo_porcentaje
    ? +(base * line.recargo_porcentaje / 100).toFixed(2)
    : 0;
  const retencionCuota = line.retencion_porcentaje
    ? +(base * line.retencion_porcentaje / 100).toFixed(2)
    : 0;

  return { cuotaIva, recargoCuota, retencionCuota };
}

// =============================================
// Recalculate invoice totals from all its lines
// =============================================

async function recalculateInvoiceTotals(invoiceId: string) {
  const { data: lines } = await supabaseAdmin
    .from('invoice_lines')
    .select('*')
    .eq('invoice_id', invoiceId);

  if (!lines) return;

  let baseImponible = 0;
  let totalImpuestos = 0;
  let totalRetenciones = 0;
  let totalSuplidos = 0;

  for (const line of lines) {
    const base = +line.base_linea;
    if (line.es_suplido) {
      totalSuplidos += base;
    } else {
      baseImponible += base;
      totalImpuestos += +(line.cuota_iva ?? 0) + +(line.recargo_cuota ?? 0);
      totalRetenciones += +(line.retencion_cuota ?? 0);
    }
  }

  await supabaseAdmin
    .from('invoices')
    .update({
      base_imponible: +baseImponible.toFixed(2),
      total_impuestos: +totalImpuestos.toFixed(2),
      total_retenciones: +totalRetenciones.toFixed(2),
      total_suplidos: +totalSuplidos.toFixed(2),
      total_factura: +(baseImponible + totalImpuestos - totalRetenciones + totalSuplidos).toFixed(2),
      updated_at: new Date().toISOString(),
    })
    .eq('id', invoiceId);
}

// =============================================
// Handler
// =============================================

export default withAuth(async (req, res, _user) => {
  try {
    switch (req.method) {
      case 'GET':
        return handleGet(req, res);
      case 'POST':
        return handleCreate(req, res);
      case 'PUT':
        return handleUpdate(req, res);
      case 'DELETE':
        return handleDelete(req, res);
      default:
        return res.status(405).json({ error: 'Método no permitido' });
    }
  } catch (error: any) {
    console.error('[API invoice-lines] Unexpected error:', error);
    return res.status(500).json({ error: error.message || 'Error interno del servidor' });
  }
});

// =============================================
// GET: List lines for an invoice
// =============================================

async function handleGet(req: NextApiRequest, res: NextApiResponse) {
  const { invoice_id } = req.query;

  if (!invoice_id) {
    return res.status(400).json({ error: 'invoice_id es requerido' });
  }

  const { data, error } = await supabaseAdmin
    .from('invoice_lines')
    .select('*')
    .eq('invoice_id', invoice_id as string)
    .order('orden', { ascending: true });

  if (error) {
    console.error('[API invoice-lines] GET error:', error);
    return res.status(500).json({ error: `Error al listar líneas: ${error.message}` });
  }

  return res.status(200).json({ data: data ?? [] });
}

// =============================================
// POST: Add a line to an invoice (borrador only)
// =============================================

async function handleCreate(req: NextApiRequest, res: NextApiResponse) {
  const { invoice_id, ...lineInput } = req.body as InvoiceLineCreateInput & { invoice_id: string };

  if (!invoice_id) return res.status(400).json({ error: 'invoice_id es requerido' });
  if (!lineInput.concepto) return res.status(400).json({ error: 'concepto es requerido' });
  if (lineInput.cantidad == null) return res.status(400).json({ error: 'cantidad es requerida' });
  if (lineInput.precio_unitario == null) return res.status(400).json({ error: 'precio_unitario es requerido' });

  // Verify invoice is borrador
  const { data: invoice, error: invoiceErr } = await supabaseAdmin
    .from('invoices')
    .select('id, estado')
    .eq('id', invoice_id)
    .single();

  if (invoiceErr || !invoice) {
    return res.status(404).json({ error: 'Factura no encontrada' });
  }
  if (invoice.estado !== 'borrador') {
    return res.status(400).json({ error: 'Solo se pueden añadir líneas a facturas en borrador' });
  }

  // Calculate line values
  const base = calculateLineBase(lineInput);
  const { cuotaIva, recargoCuota, retencionCuota } = calculateLineTaxes(base, lineInput);

  // Get next orden
  const { data: maxOrden } = await supabaseAdmin
    .from('invoice_lines')
    .select('orden')
    .eq('invoice_id', invoice_id)
    .order('orden', { ascending: false })
    .limit(1);

  const nextOrden = lineInput.orden ?? ((maxOrden && maxOrden.length > 0 ? maxOrden[0].orden : 0) + 1);

  const lineData = {
    invoice_id,
    orden: nextOrden,
    concepto: lineInput.concepto,
    descripcion: lineInput.descripcion ?? null,
    cantidad: lineInput.cantidad,
    precio_unitario: lineInput.precio_unitario,
    descuento_porcentaje: lineInput.descuento_porcentaje ?? 0,
    base_linea: base,
    tipo_iva: lineInput.tipo_iva ?? 'IVA_GENERAL',
    porcentaje_iva: lineInput.porcentaje_iva ?? 21,
    cuota_iva: cuotaIva,
    recargo_porcentaje: lineInput.recargo_porcentaje ?? null,
    recargo_cuota: recargoCuota || null,
    retencion_porcentaje: lineInput.retencion_porcentaje ?? null,
    retencion_cuota: retencionCuota || null,
    es_suplido: lineInput.es_suplido ?? false,
    suplido_nif_tercero: lineInput.suplido_nif_tercero ?? null,
  };

  const { data, error } = await supabaseAdmin
    .from('invoice_lines')
    .insert(lineData)
    .select()
    .single();

  if (error) {
    console.error('[API invoice-lines] POST error:', error);
    return res.status(500).json({ error: `Error al crear línea: ${error.message}` });
  }

  // Recalculate invoice totals
  await recalculateInvoiceTotals(invoice_id);

  return res.status(201).json({ success: true, data });
}

// =============================================
// PUT: Update a line (borrador only)
// =============================================

async function handleUpdate(req: NextApiRequest, res: NextApiResponse) {
  const { id, ...updates } = req.body as Partial<InvoiceLineCreateInput> & { id: string };

  if (!id) return res.status(400).json({ error: 'id de línea es requerido' });

  // Fetch line and verify parent invoice is borrador
  const { data: line, error: lineErr } = await supabaseAdmin
    .from('invoice_lines')
    .select('*, invoices!inner(estado)')
    .eq('id', id)
    .single();

  if (lineErr || !line) {
    return res.status(404).json({ error: 'Línea no encontrada' });
  }

  const invoiceRecord = (line.invoices as unknown as { estado: string }) ?? null;
  if (invoiceRecord && invoiceRecord.estado !== 'borrador') {
    return res.status(400).json({ error: 'Solo se pueden editar líneas de facturas en borrador' });
  }

  // Recalculate if amounts changed
  const updateData: Record<string, unknown> = {};
  const fieldsToCopy = [
    'orden', 'concepto', 'descripcion', 'cantidad', 'precio_unitario',
    'descuento_porcentaje', 'tipo_iva', 'porcentaje_iva', 'recargo_porcentaje',
    'retencion_porcentaje', 'es_suplido', 'suplido_nif_tercero',
  ];

  for (const field of fieldsToCopy) {
    if ((updates as Record<string, unknown>)[field] !== undefined) {
      updateData[field] = (updates as Record<string, unknown>)[field];
    }
  }

  // Recalculate line amounts with merged data
  const mergedLine: InvoiceLineCreateInput = {
    cantidad: (updateData.cantidad ?? line.cantidad) as number,
    precio_unitario: (updateData.precio_unitario ?? line.precio_unitario) as number,
    descuento_porcentaje: (updateData.descuento_porcentaje ?? line.descuento_porcentaje) as number,
    porcentaje_iva: (updateData.porcentaje_iva ?? line.porcentaje_iva) as number,
    recargo_porcentaje: (updateData.recargo_porcentaje ?? line.recargo_porcentaje) as number | undefined,
    retencion_porcentaje: (updateData.retencion_porcentaje ?? line.retencion_porcentaje) as number | undefined,
    concepto: (updateData.concepto ?? line.concepto) as string,
    orden: (updateData.orden ?? line.orden) as number,
    es_suplido: (updateData.es_suplido ?? line.es_suplido) as boolean,
  };

  const base = calculateLineBase(mergedLine);
  const { cuotaIva, recargoCuota, retencionCuota } = calculateLineTaxes(base, mergedLine);

  updateData.base_linea = base;
  updateData.cuota_iva = cuotaIva;
  updateData.recargo_cuota = recargoCuota || null;
  updateData.retencion_cuota = retencionCuota || null;

  const { data, error } = await supabaseAdmin
    .from('invoice_lines')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[API invoice-lines] PUT error:', error);
    return res.status(500).json({ error: `Error al actualizar línea: ${error.message}` });
  }

  // Recalculate invoice totals
  await recalculateInvoiceTotals(line.invoice_id);

  return res.status(200).json({ success: true, data });
}

// =============================================
// DELETE: Remove a line (borrador only)
// =============================================

async function handleDelete(req: NextApiRequest, res: NextApiResponse) {
  const { id } = req.body;

  if (!id) return res.status(400).json({ error: 'id de línea es requerido' });

  // Fetch line and verify parent invoice is borrador
  const { data: line, error: lineErr } = await supabaseAdmin
    .from('invoice_lines')
    .select('id, invoice_id, invoices!inner(estado)')
    .eq('id', id)
    .single();

  if (lineErr || !line) {
    return res.status(404).json({ error: 'Línea no encontrada' });
  }

  const invoiceRecord = (line.invoices as unknown as { estado: string }) ?? null;
  if (invoiceRecord && invoiceRecord.estado !== 'borrador') {
    return res.status(400).json({ error: 'Solo se pueden eliminar líneas de facturas en borrador' });
  }

  const { error } = await supabaseAdmin
    .from('invoice_lines')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[API invoice-lines] DELETE error:', error);
    return res.status(500).json({ error: `Error al eliminar línea: ${error.message}` });
  }

  // Recalculate invoice totals
  await recalculateInvoiceTotals(line.invoice_id);

  return res.status(200).json({ success: true, message: 'Línea eliminada' });
}
